import { useEffect, useRef } from 'react';
import DOMPurify from 'dompurify';
import { twMerge } from 'tailwind-merge';

/**
 * A meeting report (CR) as the client will read it, sanitised, optionally editable in place
 * (PLAN-LEARNING-LOOPS § 6.2, family « Compte rendu »). Extracted from the CRM sidebar so the
 * CRM and Travail show the same thing. Editing works on the DOM (contentEditable) and reports
 * the new HTML, sanitised, through `onChange`; the proposed version stays frozen server-side.
 */
const PROSE =
  'prose prose-sm max-w-none text-neutral-800 outline-none dark:prose-invert dark:text-neutral-100 ' +
  '[&_h1]:text-lg [&_h1]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 [&_h2]:border-b [&_h2]:border-neutral-200 [&_h2]:pb-1.5 ' +
  '[&_h2]:text-base [&_h2]:font-semibold dark:[&_h2]:border-neutral-800 [&_h3]:mt-4 [&_h3]:mb-1 [&_h3]:text-sm ' +
  '[&_h3]:font-semibold [&_p]:my-1.5 [&_p]:text-sm [&_p]:leading-relaxed [&_ul]:my-1 [&_ul]:pl-5 [&_li]:my-0.5 [&_li]:text-sm';

function sanitizeReportHtml(html: string): string {
  return DOMPurify.sanitize(html);
}

export function MeetingReportView({
  html,
  editable = false,
  onChange,
  className,
}: {
  html: string;
  editable?: boolean;
  onChange?: (html: string) => void;
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  // The DOM owns the text while it is edited: it is (re)written only when the source changes.
  useEffect(() => {
    if (ref.current) ref.current.innerHTML = sanitizeReportHtml(html);
  }, [html]);

  return (
    <div
      ref={ref}
      role={editable ? 'textbox' : undefined}
      aria-multiline={editable ? true : undefined}
      aria-label={editable ? 'Compte rendu, modifiable' : undefined}
      contentEditable={editable}
      suppressContentEditableWarning
      data-sensitive-text=""
      onInput={editable && onChange ? (e) => onChange(sanitizeReportHtml(e.currentTarget.innerHTML)) : undefined}
      className={twMerge(
        PROSE,
        editable && 'min-h-[240px] rounded-lg bg-blue-50/30 p-2 ring-1 ring-inset ring-blue-200 dark:bg-blue-950/20 dark:ring-blue-900',
        className,
      )}
    />
  );
}
